import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'How to set up Windows passwordless login',
    metaDescription:
      'Replace the Windows password prompt with phishing-resistant Monosign passwordless flows. Four-step walkthrough with deep-link to docs.',
    heroEyebrow: 'How-to · Windows passwordless',
    heroTitle: ['Retire the', 'Windows password.'],
    heroBody:
      "Replace the Windows password prompt with a Monofor Identity push. Cryptographically bound to the user's mobile device, faster than typing, and unphishable.",
    badges: ['Windows credential provider', 'Mobile-bound factor', '~7 minutes to read'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the full docs',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'From password tile to push approval.',
    stepsBody:
      'The lifecycle for a clean rollout: prep AD, deploy the client, enroll the user, verify.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Prepare Active Directory for passwordless flows',
        body: 'Confirm the Monosign user-source mappings, ensure the relevant users are synced, and align AD object permissions with the passwordless requirement set. The configuration guide flags every checkbox you need.',
        hint: 'Watch out for the AdminSDHolder permission issue on tightly-locked-down domains; the help center has a dedicated remediation article.',
      },
      {
        title: 'Install the Monofor Identity Client on the endpoint',
        body: 'Push the Windows client through your MDM or run the installer manually. It registers as a Windows credential provider so it shows up alongside (or replaces) the password tile.',
      },
      {
        title: 'Enroll the user from Monofor Identity (mobile)',
        body: 'On the Monofor Identity app, scan the QR shown on the workstation. The mobile device becomes a hardware-backed factor for the desktop sign-in.',
      },
      {
        title: 'Verify the passwordless logon',
        body: 'Lock the workstation, pick the Monofor tile, and approve the push from the mobile device. The desktop unlocks without a password prompt, and the event lands in the Monosign audit log.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Passwordless & MFA', description: 'Use case: retiring passwords for good.', href: '/solutions/passwordless-mfa' },
      { label: 'Monofor Identity', description: 'The mobile authenticator behind the approval.', href: '/sign/authenticator' },
      { label: 'For End Users', description: 'What this feels like from the user side.', href: '/for/end-users' },
    ],
  },
  tr: {
    metaTitle: 'Windows parolasız oturum açma nasıl kurulur',
    metaDescription:
      'Windows parola ekranını kimlik avına dirençli Monosign parolasız akışlarıyla değiştirin. Dört adımlık rehber ve doküman bağlantısı.',
    heroEyebrow: 'Rehber · Windows parolasız giriş',
    heroTitle: ['Windows parolasını', 'emekliye ayırın.'],
    heroBody:
      'Windows parola ekranını Monofor Identity push onayıyla değiştirin. Kullanıcının mobil cihazına kriptografik olarak bağlı, yazmaktan hızlı ve kimlik avına kapalı.',
    badges: ['Windows credential provider', 'Mobil cihaza bağlı faktör', '~7 dakikalık okuma'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Dokümantasyonu açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: 'Parola kutucuğundan push onayına.',
    stepsBody:
      "Temiz bir yaygınlaştırmanın yaşam döngüsü: AD'yi hazırlayın, istemciyi dağıtın, kullanıcıyı kaydedin, doğrulayın.",
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: "Active Directory'yi parolasız akışlara hazırlayın",
        body: 'Monosign kullanıcı kaynağı eşlemelerini doğrulayın, ilgili kullanıcıların senkronize olduğundan emin olun ve AD nesne izinlerini parolasız gereksinim setiyle hizalayın. Yapılandırma rehberi işaretlemeniz gereken her kutuyu gösterir.',
        hint: "Sıkı kilitlenmiş domain'lerde AdminSDHolder izin sorununa dikkat edin; yardım merkezinde buna özel bir çözüm makalesi var.",
      },
      {
        title: "Uç noktaya Monofor Identity Client'ı kurun",
        body: 'Windows istemcisini MDM üzerinden dağıtın veya yükleyiciyi elle çalıştırın. Windows credential provider olarak kaydolur; parola kutucuğunun yanında görünür (veya onun yerini alır).',
      },
      {
        title: 'Kullanıcıyı Monofor Identity (mobil) üzerinden kaydedin',
        body: 'Monofor Identity uygulamasında, iş istasyonunda gösterilen QR kodunu okutun. Mobil cihaz, masaüstü oturum açma için donanım destekli bir faktör hâline gelir.',
      },
      {
        title: 'Parolasız oturum açmayı doğrulayın',
        body: 'İş istasyonunu kilitleyin, Monofor kutucuğunu seçin ve mobil cihazdan gelen push bildirimini onaylayın. Masaüstü parola sorulmadan açılır ve olay Monosign denetim loguna düşer.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Parolasız ve MFA', description: 'Kullanım senaryosu: parolaları kalıcı olarak emekliye ayırmak.', href: '/solutions/passwordless-mfa' },
      { label: 'Monofor Identity', description: 'Onayın arkasındaki mobil kimlik doğrulayıcı.', href: '/sign/authenticator' },
      { label: 'Son kullanıcılar için', description: 'Kullanıcı tarafından nasıl görünür ve hissettirir.', href: '/for/end-users' },
    ],
  },
}

type Props = { locale: string }

export default function WindowsPasswordlessPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/windows-passwordless-login"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign/windows-passwordless-login' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign/windows-passwordless-login"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
